import React, { useEffect, useState } from 'react';
import { ArrowRight, ArrowDown } from 'lucide-react';

const heroImages = [
  {
    url: '/hero/hero-1.webp',
    label: 'Dhyan Chand Nagar Van · Jhansi',
    cls: 'cell-wide'
  },
  {
    url: '/hero/hero-2.webp',
    label: 'Kukrail Forest Reserve · Lucknow',
    cls: 'cell-a'
  },
  {
    url: '/hero/hero-3.webp',
    label: 'Chopta Selfie Point · Uttarakhand',
    cls: 'cell-b'
  },
  {
    url: '/hero/hero-4.webp',
    label: 'Lucknow Zoo · Butterfly Park',
    cls: 'cell-c'
  },
  {
    url: '/hero/hero-5.webp',
    label: 'Pilibhit Tiger Reserve',
    cls: 'cell-d'
  },
  {
    url: '/hero/hero-6.webp',
    label: 'Lakshmanpuri Eco Reserve',
    cls: 'cell-e'
  }
];

const Hero = () => {
  const [counters, setCounters] = useState({ projects: 0, divisions: 0, companies: 0 });

  useEffect(() => {
    const animateCounter = (target, key, duration = 1600) => {
      const increment = target / (duration / 40);
      let current = 0;
      const timer = setInterval(() => {
        current += increment;
        if (current >= target) {
          current = target;
          clearInterval(timer);
        }
        setCounters(prev => ({ ...prev, [key]: Math.floor(current) }));
      }, 40);
      return timer;
    };

    const t1 = setTimeout(() => animateCounter(50, 'projects'), 700);
    const t2 = setTimeout(() => animateCounter(15, 'divisions'), 900);
    const t3 = setTimeout(() => animateCounter(4, 'companies'), 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <section id="home" className="hero" data-testid="hero-section">
      <div className="hero-inner">
        {/* Text panel */}
        <div className="hero-text" data-testid="hero-text-panel">
          <span className="overline hero-overline">Eco-Tourism · Wildlife Art · Conservation</span>
          <h1 className="display-large hero-title">
            Where Nature Meets <em>Innovation</em>
          </h1>
          <p className="body-large hero-sub">
            We craft immersive eco-tourism landscapes, monumental wildlife sculptures, and
            nature interpretation experiences across India.
          </p>
          <div className="hero-buttons">
            <a href="#portfolio" className="btn-primary" data-testid="hero-explore-btn">
              Explore Our Work <ArrowRight size={18} style={{ marginLeft: '10px' }} />
            </a>
            <a href="#contact" className="btn-secondary" data-testid="hero-partner-btn">
              Partner With Us
            </a>
          </div>

          <div className="hero-stats" data-testid="hero-stats">
            <div className="hero-stat">
              <span className="hero-stat-num">{counters.projects}+</span>
              <span className="hero-stat-label">Projects Delivered</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">{counters.divisions}+</span>
              <span className="hero-stat-label">Forest Divisions</span>
            </div>
            <div className="hero-stat">
              <span className="hero-stat-num">{counters.companies}</span>
              <span className="hero-stat-label">Partner Studios</span>
            </div>
          </div>
        </div>

        {/* Bento image gallery */}
        <div className="hero-gallery" data-testid="hero-gallery">
          {heroImages.map((img, i) => (
            <figure
              key={img.cls}
              className={`hero-cell ${img.cls}`}
              style={{ animationDelay: `${0.15 + i * 0.1}s` }}
              data-testid={`hero-gallery-image-${i}`}
            >
              <img src={img.url} alt={img.label} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" fetchPriority={i === 0 ? 'high' : 'low'} />
              <figcaption>{img.label}</figcaption>
            </figure>
          ))}
        </div>
      </div>

      <a href="#portfolio" className="hero-scroll" data-testid="hero-scroll-cue">
        <span>Scroll to explore</span>
        <ArrowDown size={16} />
      </a>

      <style jsx>{`
        .hero {
          position: relative;
          padding: 150px 0 80px;
          background:
            radial-gradient(1200px 600px at 85% -10%, rgba(157, 176, 140, 0.18), transparent 60%),
            var(--bg-page);
          overflow: hidden;
        }

        .hero-inner {
          max-width: 1320px;
          margin: 0 auto;
          padding: 0 28px;
          display: grid;
          grid-template-columns: 0.92fr 1.08fr;
          gap: 56px;
          align-items: center;
        }

        .hero-text { max-width: 540px; }

        .hero-overline {
          display: block;
          margin-bottom: 26px;
          animation: fadeInUp 0.8s ease both;
        }

        .hero-title {
          margin: 0 0 26px;
          animation: fadeInUp 0.9s ease both;
          animation-delay: 0.1s;
        }

        .hero-title em {
          font-style: normal;
          font-weight: 700;
          color: var(--brand-accent);
        }

        .hero-sub {
          margin: 0 0 34px;
          animation: fadeInUp 0.9s ease both;
          animation-delay: 0.2s;
        }

        .hero-buttons {
          display: flex;
          gap: 14px;
          flex-wrap: wrap;
          animation: fadeInUp 0.9s ease both;
          animation-delay: 0.3s;
        }

        .hero-stats {
          display: flex;
          gap: 40px;
          margin-top: 48px;
          padding-top: 30px;
          border-top: 1px solid var(--border-medium);
          animation: fadeInUp 0.9s ease both;
          animation-delay: 0.4s;
        }

        .hero-stat { display: flex; flex-direction: column; gap: 4px; }

        .hero-stat-num {
          font-family: 'Source Sans 3', sans-serif;
          font-size: 2.4rem;
          font-weight: 700;
          line-height: 1;
          color: var(--brand-primary);
        }

        .hero-stat-label {
          font-size: 0.78rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--text-light);
        }

        /* Bento gallery */
        .hero-gallery {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          grid-template-rows: repeat(3, minmax(120px, 1fr));
          gap: 12px;
          height: 78vh;
          min-height: 540px;
          max-height: 680px;
        }

        .cell-wide { grid-column: 1 / 3; grid-row: 1 / 2; }
        .cell-a    { grid-column: 3 / 4; grid-row: 1 / 3; }
        .cell-b    { grid-column: 1 / 2; grid-row: 2 / 3; }
        .cell-c    { grid-column: 2 / 3; grid-row: 2 / 3; }
        .cell-d    { grid-column: 1 / 2; grid-row: 3 / 4; }
        .cell-e    { grid-column: 2 / 4; grid-row: 3 / 4; }

        .hero-cell {
          position: relative;
          margin: 0;
          overflow: hidden;
          border-radius: var(--radius-img);
          background: var(--bg-subtle);
          opacity: 0;
          animation: heroReveal 1s cubic-bezier(0.25, 1, 0.5, 1) both;
          box-shadow: 0 12px 30px rgba(26, 33, 24, 0.08);
        }

        .hero-cell img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.9s cubic-bezier(0.25, 1, 0.5, 1);
        }

        .hero-cell:hover img { transform: scale(1.07); }

        .hero-cell figcaption {
          position: absolute;
          left: 0;
          bottom: 0;
          right: 0;
          padding: 26px 14px 12px;
          font-family: 'Source Sans 3', sans-serif;
          font-size: 0.72rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          color: #fff;
          background: linear-gradient(to top, rgba(26, 33, 24, 0.78), transparent);
          opacity: 0;
          transform: translateY(8px);
          transition: opacity 0.4s ease, transform 0.4s ease;
        }

        .hero-cell:hover figcaption { opacity: 1; transform: translateY(0); }

        .hero-scroll {
          position: absolute;
          left: 50%;
          bottom: 24px;
          transform: translateX(-50%);
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--text-light);
          text-decoration: none;
          font-size: 0.72rem;
          text-transform: uppercase;
          letter-spacing: 0.18em;
          animation: fadeIn 1.2s ease both;
          animation-delay: 0.8s;
        }

        .hero-scroll :global(svg) { animation: bob 1.8s ease-in-out infinite; }

        @keyframes bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(5px); }
        }

        @media (max-width: 1024px) {
          .hero-inner {
            grid-template-columns: 1fr;
            gap: 44px;
          }
          .hero-text { max-width: 100%; }
          .hero-gallery { height: 64vh; min-height: 460px; }
          .hero-scroll { display: none; }
        }

        @media (max-width: 640px) {
          .hero { padding: 120px 0 56px; }
          .hero-gallery {
            grid-template-columns: repeat(2, 1fr);
            grid-template-rows: repeat(4, minmax(110px, 1fr));
            height: auto;
            min-height: 0;
            max-height: none;
          }
          .cell-wide { grid-column: 1 / 3; grid-row: 1 / 2; }
          .cell-a    { grid-column: 1 / 2; grid-row: 2 / 3; }
          .cell-b    { grid-column: 2 / 3; grid-row: 2 / 3; }
          .cell-c    { grid-column: 1 / 2; grid-row: 3 / 4; }
          .cell-d    { grid-column: 2 / 3; grid-row: 3 / 4; }
          .cell-e    { grid-column: 1 / 3; grid-row: 4 / 5; }
          .hero-stats { gap: 26px; flex-wrap: wrap; }
          .hero-stat-num { font-size: 2rem; }
          .hero-buttons { flex-direction: column; }
          .hero-buttons :global(a) { width: 100%; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
